/**
 * Class-name composition, the shadcn/ui way.
 *
 * `clsx` resolves the conditionals; `twMerge` resolves the CONFLICTS — the last
 * `px-*` wins rather than both landing in the attribute and the cascade deciding
 * by declaration order. Without it, a variant's padding and an override's
 * padding both apply and which one shows depends on the order Tailwind emitted
 * them in, which changes between builds.
 *
 * THE MERGER HAS TO BE TOLD ABOUT THE DESIGN'S OWN FONT SIZES.
 *
 * tailwind-merge decides which group a class belongs to by its shape, and both
 * a font size and a text colour are spelled `text-*`. For the stock scale it
 * can tell them apart: `text-sm` looks like a size, `text-red-500` does not. A
 * design token cannot be recognised that way — `text-card-title` looks exactly
 * like a colour called "card-title" — so it was filed under text-colour, put in
 * the same group as `text-foreground`, and one of the two was dropped as a
 * conflict.
 *
 * That is not a theoretical risk. `cn("text-card-title text-foreground")`
 * returned `"text-foreground"`, so every typography component in the kit
 * rendered at its inherited size and the hierarchy they exist to create was
 * silently removed at the last step. The unit test in the web app asserts the
 * three tiers keep both of their classes, which is what caught it.
 *
 * The list below must stay in step with the semantic scale in kit.css. A role
 * added there and forgotten here does not error; it just stops taking effect
 * wherever it is combined with an ink, which is everywhere.
 *
 * WHICH IS EXACTLY WHAT HAPPENED TO `figure` AND `otp`.
 *
 * kit.css declares ten roles; this list had eight. The two it was missing are
 * the two largest sizes in the product — the reporting dashboard's KPI at
 * 34px and the OTP cells at 24px — and both are written alongside an ink:
 *
 *   figure-card.tsx   cn("text-figure text-foreground tabular-nums")
 *   code-step.tsx     cn("text-otp text-foreground …")
 *
 * so both were being filed under text-colour, treated as a conflict with the
 * ink beside them, and dropped. The KPI a reporting card exists to show has
 * been rendering at whatever it inherited, which is the same defect kit.css
 * added `--text-figure` to fix in the first place.
 *
 * THE FAMILY UTILITIES DO NOT NEED AN ENTRY, and it is worth writing down why
 * so nobody adds a redundant one. `font-family-prose`, `font-family-ui` and
 * `font-family-mono` are matched by tailwind-merge's OWN `font-family` group,
 * which is `font-` followed by anything, so the three already conflict with
 * each other correctly. `font-medium` and `font-semibold` are matched by the
 * separate `font-weight` group ahead of it, so a family and a weight on one
 * element both survive. Verified rather than assumed — see cn.test.ts.
 */

import { type ClassValue, clsx } from "clsx"
import { extendTailwindMerge } from "tailwind-merge"

const twMerge = extendTailwindMerge({
	extend: {
		classGroups: {
			"font-size": [
				{
					text: [
						"page-title",
						"section-title",
						"card-title",
						"body",
						"description",
						"field-label",
						"meta",
						"overline",
						"figure",
						"otp",
						"longform",
					],
				},
			],
		},
	},
})

export function cn(...inputs: ClassValue[]): string {
	return twMerge(clsx(inputs))
}
